import {
  Activity,
  Boxes,
  FileUp,
  Grid2x2,
  Inbox,
  LayoutList,
  Layers,
  ListChecks,
  Radar,
  Tags,
} from "lucide-react";
import { Suspense, lazy } from "react";
import { NavLink, Navigate, Route, Routes } from "react-router-dom";

import { useConfig, useHealth, useStatusCounts, useSubmitJob } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { ErrorNote } from "@/components/ui/panel";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { cn } from "@/lib/utils";
import Buckets from "@/routes/Buckets";
import Clusters from "@/routes/Clusters";
import Concepts from "@/routes/Concepts";
import Ingest from "@/routes/Ingest";
import IngestRun from "@/routes/IngestRun";
import Probe from "@/routes/Probe";
import Picker from "@/routes/Picker";
import Questions from "@/routes/Questions";
import Review from "@/routes/Review";
import Stacks from "@/routes/Stacks";

/**
 * Metrics is the only screen that pulls in a charting library, and it more than
 * doubled the bundle when it was imported eagerly. Splitting it keeps that weight off
 * the four screens reviewers actually live in — §10's own argument for building them
 * in order of daily work carried.
 */
const Metrics = lazy(() => import("@/routes/Metrics"));

/**
 * ARCHITECTURE §10: build screens in order of daily work carried. Review is first
 * because it is the one people live in, and it was the weakest view in Streamlit —
 * `st.number_input("open question #")` to open a record was the ceiling showing.
 *
 * The second group is the engineering instruments. They are separated because nobody
 * outside the team opens them, not because they are less finished — the probe is how
 * a threshold change gets judged and Metrics is where a precision regression shows up.
 */
const NAV = [
  { to: "/review", label: "Review", icon: Inbox },
  { to: "/questions", label: "Questions", icon: LayoutList },
  { to: "/clusters", label: "Clusters", icon: Layers },
  { to: "/ingest", label: "Ingest", icon: FileUp },
];

const TOOLS = [
  { to: "/picker", label: "Picker", icon: ListChecks },
  { to: "/probe", label: "Probe", icon: Radar },
  { to: "/metrics", label: "Metrics", icon: Activity },
];

/**
 * Neither Daily nor Instruments fits: this is occasional reference-data curation
 * (add a stack, promote a concept a reviewer keeps seeing flagged as a candidate),
 * not queue work or read-only diagnostics. "Taxonomy" is the term DESIGN.md and
 * core/taxonomy.py already use — CLAUDE.md's "domain vocabulary matches the spec".
 */
const TAXONOMY = [
  { to: "/stacks", label: "Stacks", icon: Boxes },
  { to: "/concepts", label: "Concepts", icon: Tags },
  { to: "/buckets", label: "Buckets", icon: Grid2x2 },
];

/**
 * The active item gets a wash and a 2px stripe at the rail edge rather than a grey
 * fill. A fill in a neutral palette reads as "hovered" as easily as "here", and the
 * stripe survives being glanced at from across a desk.
 */
function NavItem({ to, label, icon: Icon, badge }: (typeof NAV)[number] & { badge?: React.ReactNode }) {
  return (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) =>
          cn(
            "relative flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition-colors",
            isActive ? "bg-bank-accent-wash text-bank-accent-ink font-medium" : "hover:bg-bank-surface-2",
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && (
              <span
                className="bg-bank-accent absolute top-1.5 bottom-1.5 -left-2 w-0.5 rounded-r"
                aria-hidden="true"
              />
            )}
            <Icon
              className={cn("size-4 shrink-0", isActive ? "text-bank-accent" : "text-bank-muted")}
              aria-hidden="true"
            />
            {label}
            {badge}
          </>
        )}
      </NavLink>
    </li>
  );
}

/**
 * §8.6's daily cap is the thing to check before starting a run, and "14/20" reads
 * slower than a bar that is most of the way across. Amber past 70% because that is
 * the point where a pipeline run may not finish on what is left.
 */
function QuotaMeter({ model, used, cap }: { model: string; used: number; cap: number }) {
  const share = cap > 0 ? Math.min(used / cap, 1) : 0;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-bank-muted truncate font-mono text-[11px]" title={model}>
          {model}
        </span>
        <span className="font-mono text-[11px] tabular-nums">
          {used}/{cap}
        </span>
      </div>
      <div className="bg-bank-surface-3 h-[3px] overflow-hidden rounded-full">
        <div
          className={cn("h-full rounded-full", share > 0.7 ? "bg-bank-attention" : "bg-bank-accent")}
          style={{ width: `${share * 100}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Rows sitting between `extracted` and `dedup_checked` — carried this far by a
 * requeue, a re-enrich, or a fresh ingest, and going nowhere until something submits
 * a `pipeline` job. Ambient because a requeue on Review or Questions leaves this the
 * only place that still says so once the reviewer has moved on.
 */
function PipelineBacklog() {
  const { data } = useStatusCounts();
  const submit = useSubmitJob();
  const backlog =
    (data?.counts.extracted ?? 0) +
    (data?.counts.enriched ?? 0) +
    (data?.counts.embedded ?? 0) +
    (data?.counts.dedup_checked ?? 0);
  if (!backlog) return null;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span
          className="text-bank-muted font-mono text-[11px] tabular-nums"
          title="extracted + enriched + embedded + dedup_checked — short of pending_review"
        >
          {backlog} waiting on pipeline
        </span>
        <Button size="sm" disabled={submit.isPending} onClick={() => submit.mutate("pipeline")}>
          {submit.isPending ? "Queuing…" : "Run"}
        </Button>
      </div>
      <ErrorNote error={submit.error} />
    </div>
  );
}

/**
 * What Streamlit's sidebar carried: which models are answering, under which dedup
 * config_version, and how much of today's quota is gone.
 *
 * Quota lines appear only for models that have actually been called today — §8.6's
 * cap is the thing to check before starting a run, and a row of "0/1000" every
 * morning trains people to stop reading it.
 */
function ConfigNote() {
  const { data } = useConfig();
  if (!data) return null;
  const spent = data.quota.filter((q) => q.used > 0);
  return (
    <div className="space-y-3">
      {spent.length > 0 && (
        <div className="space-y-2.5">
          {spent.map((q) => (
            <QuotaMeter key={q.model} model={q.model} used={q.used} cap={q.daily_cap} />
          ))}
        </div>
      )}
      <dl className="text-bank-muted grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1 font-mono text-[11px]">
        <dt className="opacity-75">llm</dt>
        <dd
          className="text-bank-ink truncate"
          title={`${data.backend} · ${data.llm_model} · adjudication ${data.adjudication_model}`}
        >
          {data.llm_model}
        </dd>
        <dt className="opacity-75">emb</dt>
        <dd className="text-bank-ink truncate" title={data.embedding_model}>
          {data.embedding_dimensions}d
        </dd>
        <dt className="opacity-75">dedup</dt>
        <dd className="text-bank-ink truncate" title={`dedup config_version ${data.dedup_config_version}`}>
          {data.dedup_config_version.slice(0, 8)}
        </dd>
      </dl>
    </div>
  );
}

function HealthDot() {
  const { data, isError } = useHealth();
  const ok = !isError && data?.status === "ok";
  const label = isError
    ? "API unreachable"
    : ok
      ? `database reachable · ${data?.latency_ms ?? "?"} ms`
      : (data?.database ?? "checking");
  return (
    <span className="text-bank-muted flex items-center gap-1.5 text-[11px]" title={label}>
      <span className={cn("size-1.5 rounded-full", ok ? "bg-bank-good" : "bg-bank-bad")} aria-hidden="true" />
      {ok ? `healthy · ${data?.latency_ms ?? "?"} ms` : "degraded"}
    </span>
  );
}

function PendingCount() {
  const { data } = useStatusCounts();
  const pending = (data?.counts.pending_review ?? 0) + (data?.counts.stale ?? 0);
  if (!pending) return null;
  return (
    <span className="bg-bank-attention-wash text-bank-attention-ink ml-auto rounded px-1.5 py-0.5 font-mono text-[11px] font-medium tabular-nums">
      {pending}
    </span>
  );
}

export default function App() {
  return (
    <div className="flex h-full">
      <nav className="border-bank-border bg-bank-surface flex w-60 shrink-0 flex-col border-r">
        <div className="border-bank-border flex items-center gap-2.5 border-b px-4 py-3">
          <span className="bg-bank-accent-strong text-bank-on-accent grid size-6.5 shrink-0 place-items-center rounded-md font-mono text-xs font-semibold">
            QB
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold">Question Bank</p>
            <HealthDot />
          </div>
        </div>

        <div className="p-2">
          <p className="text-bank-muted px-2 pt-1.5 pb-2 font-mono text-[10px] tracking-[0.1em] uppercase">
            Daily
          </p>
          <ul className="space-y-0.5">
            {NAV.map((item) => (
              <NavItem key={item.to} {...item} badge={item.to === "/review" ? <PendingCount /> : null} />
            ))}
          </ul>
        </div>

        <div className="p-2 pt-0">
          <p className="text-bank-muted px-2 pt-1.5 pb-2 font-mono text-[10px] tracking-[0.1em] uppercase">
            Taxonomy
          </p>
          <ul className="space-y-0.5">
            {TAXONOMY.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </ul>
        </div>

        <div className="flex-1 p-2 pt-0">
          <p className="text-bank-muted px-2 pt-1.5 pb-2 font-mono text-[10px] tracking-[0.1em] uppercase">
            Instruments
          </p>
          <ul className="space-y-0.5">
            {TOOLS.map((item) => (
              <NavItem key={item.to} {...item} />
            ))}
          </ul>
        </div>

        <div className="border-bank-border space-y-3 border-t px-4 py-3">
          <PipelineBacklog />
          <ConfigNote />
        </div>

        <div className="border-bank-border flex items-center justify-between border-t px-4 py-2.5">
          <span className="text-bank-muted font-mono text-[10px] tracking-[0.1em] uppercase">Theme</span>
          <ThemeToggle />
        </div>
      </nav>

      <main className="min-w-0 flex-1 overflow-auto">
        <Suspense fallback={<p className="text-bank-muted p-4 text-sm">Loading…</p>}>
          <Routes>
            <Route path="/" element={<Navigate to="/review" replace />} />
            <Route path="/review" element={<Review />} />
            <Route path="/questions" element={<Questions />} />
            <Route path="/clusters" element={<Clusters />} />
            <Route path="/ingest" element={<Ingest />} />
            <Route path="/ingest/runs/:sourceDoc" element={<IngestRun />} />
            <Route path="/stacks" element={<Stacks />} />
            <Route path="/concepts" element={<Concepts />} />
            <Route path="/buckets" element={<Buckets />} />
            <Route path="/picker" element={<Picker />} />
            <Route path="/probe" element={<Probe />} />
            <Route path="/metrics" element={<Metrics />} />
            <Route path="*" element={<Navigate to="/review" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
